import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Zap, Play, List, Info, Clock, User, XCircle,
  GitBranch, PauseCircle, Copy, Eye, AlertCircle, RotateCcw,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { Button } from '../components/ui/Button.tsx';
import { Badge, AutomationStatusBadge } from '../components/ui/Badge.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { Pagination } from '../components/ui/Pagination.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { InstanceLogModal } from '../components/journey/InstanceLogModal.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Trigger {
  id: string;
  triggerType: string;
  eventType?: string | null;
  scheduleExpression?: string | null;
}

interface AutomationDefinition {
  id: string;
  name: string;
  description: string | null;
  type: 'journey' | 'campaign';
  status: string;
  concurrencyMode: string | null;
  definition: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  triggers?: Trigger[];
}

interface AutomationInstance {
  id: string;
  customerId: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  currentStep: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-zinc-50 last:border-0">
      <span className="text-xs text-zinc-500 shrink-0">{label}</span>
      <span className="text-xs text-zinc-800 font-medium text-right break-all">{value ?? '—'}</span>
    </div>
  );
}

const INSTANCE_STATUS_MAP: Record<
  string,
  'success' | 'danger' | 'warning' | 'default' | 'info' | 'purple'
> = {
  completed: 'success',
  failed: 'danger',
  running: 'purple',
  cancelled: 'default',
  pending: 'warning',
  paused: 'warning',
};

// ─── Instance filter tabs ─────────────────────────────────────────────────────

const INSTANCE_TABS = [
  { key: 'all', label: 'All' },
  { key: 'running', label: 'Running' },
  { key: 'failed', label: 'Failed' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
] as const;

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AutomationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [instancePage, setInstancePage] = useState(1);
  const instanceLimit = 50;
  const [instanceFilter, setInstanceFilter] = useState<string>('all');
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [cancelInstanceId, setCancelInstanceId] = useState<string | null>(null);
  const [triggerCustomerId, setTriggerCustomerId] = useState('');
  const [showTriggerDialog, setShowTriggerDialog] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<{
    id: string;
    customerId: string;
    status: string;
    startedAt: string | null;
    completedAt: string | null;
    errorMessage: string | null;
  } | null>(null);

  // ── queries ─────────────────────────────────────────────────────────────────

  const { data: automation, isLoading } = useQuery<AutomationDefinition>({
    queryKey: ['automation', id],
    queryFn: () => api.get(`/api/v1/automations/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  const { data: instancesData } = useQuery<{ items: AutomationInstance[]; total: number }>({
    queryKey: ['automation-instances', id, instancePage],
    queryFn: () =>
      api
        .get(`/api/v1/automations/${id}/instances`, {
          params: { page: instancePage, limit: instanceLimit },
        })
        .then((r) => r.data),
    enabled: !!id,
    refetchInterval: automation?.status === 'active' ? 15_000 : false,
  });

  const instances = instancesData?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((instancesData?.total ?? 0) / instanceLimit));

  // ── Filtered instances + tab counts ──────────────────────────────────────────

  const instanceCounts = useMemo(() => {
    const c: Record<string, number> = { all: instances.length };
    for (const inst of instances) {
      c[inst.status] = (c[inst.status] ?? 0) + 1;
    }
    return c;
  }, [instances]);

  const filteredInstances = useMemo(
    () =>
      instanceFilter === 'all'
        ? instances
        : instances.filter((i) => i.status === instanceFilter),
    [instances, instanceFilter],
  );

  const failedCount = instanceCounts['failed'] ?? 0;

  // ── mutations ────────────────────────────────────────────────────────────────

  const publishMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/automations/${id}/publish`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation', id] });
      toast.success('Automation published');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const cancelInstanceMutation = useMutation({
    mutationFn: (instanceId: string) => api.delete(`/api/v1/automations/instances/${instanceId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation-instances', id, instancePage] });
      setCancelInstanceId(null);
      toast.success('Instance cancelled');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const deactivateMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/automations/${id}/deactivate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation', id] });
      toast.success('Automation deactivated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/automations/${id}/reactivate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation', id] });
      toast.success('Automation reactivated');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const revertToDraftMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/automations/${id}/revert-to-draft`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation', id] });
      toast.success('Automation moved back to draft');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const duplicateMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/automations/${id}/duplicate`),
    onSuccess: (res) => {
      const newDef = res.data as { id: string };
      toast.success('Automation duplicated');
      navigate(`/automations/${newDef.id}/build`);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const triggerMutation = useMutation({
    mutationFn: (customerId: string) =>
      api.post('/api/v1/automations/trigger', {
        automationDefinitionId: id,
        customerId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation-instances', id, instancePage] });
      setShowTriggerDialog(false);
      setTriggerCustomerId('');
      toast.success('Journey triggered');
    },
    onError: (err) => {
      toast.error(extractError(err));
    },
  });

  // ── loading state ─────────────────────────────────────────────────────────────

  if (isLoading || !automation) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/automations')}
          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-zinc-900 truncate">{automation.name}</h1>
          {automation.description && (
            <p className="text-xs text-zinc-400 mt-0.5">{automation.description}</p>
          )}
        </div>
        <Badge variant={automation.type === 'journey' ? 'purple' : 'info'}>{automation.type === 'journey' ? 'automation' : automation.type}</Badge>
        <AutomationStatusBadge status={automation.status} />
        {automation.type === 'journey' && automation.status === 'draft' && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate(`/automations/${automation.id}/build`)}
          >
            <GitBranch size={12} className="mr-1" /> Edit in Builder
          </Button>
        )}
        {automation.type === 'journey' && (automation.status === 'active' || automation.status === 'paused') && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate(`/automations/${automation.id}/build`)}
          >
            <Eye size={12} className="mr-1" /> View in Builder
          </Button>
        )}
        {automation.status === 'draft' && (
          <Button
            size="sm"
            loading={publishMutation.isPending}
            onClick={() => setConfirmPublish(true)}
          >
            <Play size={12} className="mr-1" /> Publish
          </Button>
        )}
        {automation.status === 'active' && (
          <Button
            size="sm"
            variant="secondary"
            loading={deactivateMutation.isPending}
            onClick={() => setConfirmDeactivate(true)}
          >
            <PauseCircle size={12} className="mr-1" /> Deactivate
          </Button>
        )}
        {automation.status === 'paused' && (
          <>
            <Button
              size="sm"
              loading={reactivateMutation.isPending}
              onClick={() => reactivateMutation.mutate()}
            >
              <Play size={12} className="mr-1" /> Reactivate
            </Button>
            <Button
              size="sm"
              variant="secondary"
              loading={revertToDraftMutation.isPending}
              onClick={() => revertToDraftMutation.mutate()}
            >
              <RotateCcw size={12} className="mr-1" /> Back to Draft
            </Button>
          </>
        )}
        {automation.status !== 'archived' && (
          <Button
            size="sm"
            variant="ghost"
            loading={duplicateMutation.isPending}
            onClick={() => duplicateMutation.mutate()}
          >
            <Copy size={12} className="mr-1" /> Duplicate
          </Button>
        )}
        {automation.type === 'journey' && automation.status === 'active' && (
          <Button size="sm" variant="secondary" onClick={() => setShowTriggerDialog(true)}>
            <Zap size={12} className="mr-1" /> Trigger manually
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left — info */}
        <div className="space-y-5 lg:col-span-1">
          {/* Definition info */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-zinc-100">
              <Info size={14} className="text-zinc-400" />
              <h2 className="text-sm font-semibold text-zinc-700">Details</h2>
            </div>
            <div className="p-5">
              <KV
                label="ID"
                value={<code className="font-mono text-[10px]">{automation.id}</code>}
              />
              <KV
                label="Type"
                value={
                  <Badge variant={automation.type === 'journey' ? 'purple' : 'info'}>
                    {automation.type === 'journey' ? 'automation' : automation.type}
                  </Badge>
                }
              />
              <KV label="Status" value={<AutomationStatusBadge status={automation.status} />} />
              <KV
                label="Concurrency"
                value={
                  automation.concurrencyMode ? (
                    <Badge variant="default">{automation.concurrencyMode}</Badge>
                  ) : (
                    '—'
                  )
                }
              />
              <KV label="Created" value={fmt(automation.createdAt)} />
              <KV label="Updated" value={fmt(automation.updatedAt)} />
            </div>
          </div>

          {/* Triggers */}
          {automation.triggers && automation.triggers.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3.5 border-b border-zinc-100">
                <Zap size={14} className="text-zinc-400" />
                <h2 className="text-sm font-semibold text-zinc-700">
                  Triggers · {automation.triggers.length}
                </h2>
              </div>
              <div className="p-5 space-y-3">
                {automation.triggers.map((t) => (
                  <div key={t.id} className="p-3 bg-zinc-50 rounded-lg space-y-1">
                    <Badge variant="default">{t.triggerType}</Badge>
                    {t.eventType && (
                      <p className="text-[11px] text-zinc-500 font-mono">{t.eventType}</p>
                    )}
                    {t.scheduleExpression && (
                      <p className="text-[11px] text-zinc-500 font-mono">{t.scheduleExpression}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Definition JSON */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-zinc-100">
              <List size={14} className="text-zinc-400" />
              <h2 className="text-sm font-semibold text-zinc-700">Definition</h2>
            </div>
            <div className="p-5">
              <pre className="text-[11px] text-zinc-700 font-mono bg-zinc-50 rounded-lg p-3 overflow-auto max-h-64 whitespace-pre-wrap break-all">
                {JSON.stringify(automation.definition, null, 2)}
              </pre>
            </div>
          </div>
        </div>

        {/* Right — instances */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            {/* Card header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <List size={14} className="text-zinc-400" />
                  <div>
                    <h2 className="text-sm font-semibold text-zinc-700">
                      Instances
                      {instancesData ? ` · ${instancesData.total.toLocaleString()}` : ''}
                    </h2>
                    <p className="text-[10px] text-zinc-400 mt-0.5">
                      Click a row to view step-by-step execution log
                    </p>
                  </div>
                </div>
                {/* Failed badge */}
                {failedCount > 0 && (
                  <button
                    onClick={() => setInstanceFilter(instanceFilter === 'failed' ? 'all' : 'failed')}
                    className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium transition-colors ${
                      instanceFilter === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-700'
                    }`}
                  >
                    <AlertCircle size={10} />
                    {failedCount} failed
                  </button>
                )}
              </div>

              {/* Instance filter tabs */}
              {instances.length > 0 && (
                <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-0.5">
                  {INSTANCE_TABS.map((tab) => {
                    const count = instanceCounts[tab.key] ?? 0;
                    const active = instanceFilter === tab.key;
                    if (tab.key !== 'all' && !count) return null;
                    return (
                      <button
                        key={tab.key}
                        onClick={() => setInstanceFilter(tab.key)}
                        className={`px-2 py-0.5 text-[11px] rounded font-medium transition-colors ${
                          active
                            ? 'bg-white text-zinc-900 shadow-sm'
                            : 'text-zinc-500 hover:text-zinc-700'
                        }`}
                      >
                        {tab.label}
                        {tab.key !== 'all' && (
                          <span className="ml-1 text-[10px] text-zinc-400">{count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {filteredInstances.length === 0 && instances.length === 0 ? (
              <div className="p-10 text-center">
                <Zap size={24} className="text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">No instances yet</p>
                {automation.status === 'draft' && (
                  <p className="text-xs text-zinc-400 mt-1">
                    Publish the automation to allow it to run.
                  </p>
                )}
              </div>
            ) : filteredInstances.length === 0 ? (
              <div className="p-10 text-center">
                <p className="text-sm text-zinc-400">No {instanceFilter} instances on this page</p>
                <button
                  className="text-xs text-violet-500 hover:underline mt-1"
                  onClick={() => setInstanceFilter('all')}
                >
                  Show all
                </button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-100">
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Customer
                        </th>
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Status
                        </th>
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Current step
                        </th>
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Started
                        </th>
                        <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                          Completed
                        </th>
                        <th className="px-5 pb-2 pt-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50">
                      {filteredInstances.map((inst) => (
                        <tr
                          key={inst.id}
                          className={`transition-colors cursor-pointer ${
                            inst.status === 'failed'
                              ? 'bg-red-50/50 hover:bg-red-50'
                              : 'hover:bg-zinc-50'
                          }`}
                          onClick={() => {
                            setSelectedInstance({
                              id: inst.id,
                              customerId: inst.customerId,
                              status: inst.status,
                              startedAt: inst.startedAt,
                              completedAt: inst.completedAt,
                              errorMessage: inst.errorMessage,
                            });
                          }}
                        >
                          <td className="px-5 py-2.5">
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/customers/${inst.customerId}`); }}
                              className="flex items-center gap-1 font-mono text-[10px] text-zinc-500 hover:text-zinc-900 hover:underline"
                            >
                              <User size={10} />
                              {inst.customerId.slice(0, 8)}…
                            </button>
                          </td>
                          <td className="px-5 py-2.5">
                            <Badge variant={INSTANCE_STATUS_MAP[inst.status] ?? 'default'}>
                              {inst.status}
                            </Badge>
                            {inst.status === 'failed' && inst.errorMessage && (
                              <p
                                className="text-[10px] text-red-500 font-mono mt-0.5 truncate max-w-[180px]"
                                title={inst.errorMessage}
                              >
                                {inst.errorMessage}
                              </p>
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-zinc-400 font-mono text-[10px]">
                            {inst.currentStep ?? '—'}
                          </td>
                          <td className="px-5 py-2.5 text-zinc-500 whitespace-nowrap">
                            <span className="flex items-center gap-1">
                              <Clock size={10} className="text-zinc-300" />
                              {fmt(inst.startedAt)}
                            </span>
                          </td>
                          <td className="px-5 py-2.5 text-zinc-500 whitespace-nowrap">
                            {fmt(inst.completedAt)}
                          </td>
                          <td className="px-5 py-2.5">
                            {inst.status === 'running' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setCancelInstanceId(inst.id); }}
                                className="p-1 text-zinc-300 hover:text-red-500 transition-colors"
                                title="Cancel instance"
                              >
                                <XCircle size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="border-t border-zinc-100">
                    <Pagination
                      page={instancePage}
                      totalPages={totalPages}
                      onPageChange={setInstancePage}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirm publish */}
      <ConfirmDialog
        open={confirmPublish}
        title="Publish automation?"
        message={`"${automation.name}" will become active and start processing triggers. Draft cannot be edited after publishing.`}
        confirmLabel="Publish"
        variant="warning"
        onConfirm={() => {
          setConfirmPublish(false);
          publishMutation.mutate();
        }}
        onCancel={() => setConfirmPublish(false)}
      />

      {/* Confirm deactivate */}
      <ConfirmDialog
        open={confirmDeactivate}
        title="Deactivate automation?"
        message={`"${automation.name}" will be paused. No new triggers will fire, but running instances will complete. You can reactivate it later.`}
        confirmLabel="Deactivate"
        variant="warning"
        loading={deactivateMutation.isPending}
        onConfirm={() => {
          setConfirmDeactivate(false);
          deactivateMutation.mutate();
        }}
        onCancel={() => setConfirmDeactivate(false)}
      />

      {/* Confirm cancel instance */}
      <ConfirmDialog
        open={!!cancelInstanceId}
        title="Cancel instance?"
        message="This automation instance will be stopped immediately."
        confirmLabel="Cancel instance"
        variant="danger"
        onConfirm={() => {
          if (cancelInstanceId) cancelInstanceMutation.mutate(cancelInstanceId);
        }}
        onCancel={() => setCancelInstanceId(null)}
      />

      {/* Instance log modal */}
      {selectedInstance && id && (
        <InstanceLogModal
          automationId={id}
          instanceId={selectedInstance.id}
          customerId={selectedInstance.customerId}
          status={selectedInstance.status}
          startedAt={selectedInstance.startedAt}
          completedAt={selectedInstance.completedAt}
          errorMessage={selectedInstance.errorMessage}
          onClose={() => setSelectedInstance(null)}
        />
      )}

      {/* Manual trigger dialog */}
      {showTriggerDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm space-y-4">
            <h2 className="text-sm font-semibold text-zinc-900">Trigger automation manually</h2>
            <p className="text-xs text-zinc-500">
              Enter the customer ID to start an automation instance for that customer.
            </p>
            <input
              type="text"
              className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-zinc-300"
              placeholder="Customer ID (UUID)"
              value={triggerCustomerId}
              onChange={(e) => setTriggerCustomerId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && triggerCustomerId.trim()) {
                  triggerMutation.mutate(triggerCustomerId.trim());
                }
              }}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setShowTriggerDialog(false);
                  setTriggerCustomerId('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                loading={triggerMutation.isPending}
                disabled={!triggerCustomerId.trim()}
                onClick={() => triggerMutation.mutate(triggerCustomerId.trim())}
              >
                <Zap size={12} className="mr-1" /> Trigger
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
